import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  InputAdornment,
  Skeleton,
  Stack,
  alpha,
  useTheme,
  Button,
  IconButton,
  Tooltip,
  Grow,
  Divider,
  useMediaQuery,
  LinearProgress,
} from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  Search,
  Phone,
  CalendarMonthOutlined,
  WhatsApp,
  ArrowForward,
  AutoAwesome,
  Today,
  DateRange,
  AllInclusive,
  CelebrationOutlined,
  VolunteerActivismOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import moment from "moment";

import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import { styled, keyframes } from "@mui/system";

// ─── KEYFRAME ANIMATIONS ────────────────────────────────────────────────────

const heartbeat = keyframes`
  0%, 100% { transform: scale(1); }
  14% { transform: scale(1.15); }
  28% { transform: scale(1); }
  42% { transform: scale(1.15); }
  70% { transform: scale(1); }
`;

const floatHeart = keyframes`
  0% { transform: translateY(0) rotate(0deg); opacity: 0.6; }
  25% { transform: translateY(-15px) rotate(-5deg); opacity: 0.8; }
  50% { transform: translateY(-8px) rotate(3deg); opacity: 0.5; }
  75% { transform: translateY(-20px) rotate(-3deg); opacity: 0.7; }
  100% { transform: translateY(0) rotate(0deg); opacity: 0.6; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const ripple = keyframes`
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const gentleGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(236, 72, 153, 0.1); }
  50% { box-shadow: 0 0 30px rgba(236, 72, 153, 0.2); }
`;

// ─── COLOR PALETTE ──────────────────────────────────────────────────────────

const ANNIVERSARY_PALETTE = {
  primary: "#EC4899",       // Rose Pink
  primaryLight: "#F9A8D4",  // Soft Pink
  primaryDark: "#BE185D",   // Deep Rose
  accent: "#A855F7",        // Purple accent
  accentLight: "#C084FC",   // Light Purple
  glow: "#FDF2F8",          // Pink glow
  warmGlow: "#FFF1F2",      // Warm rose
  gradientStart: "#EC4899",
  gradientEnd: "#A855F7",
  surface: "rgba(236, 72, 153, 0.03)",
};

// ─── FLOATING HEARTS COMPONENT ──────────────────────────────────────────────

const FloatingHeart = styled("div")(({ delay, left, size }) => ({
  position: "absolute",
  left: `${left}%`,
  bottom: "10%",
  fontSize: size || 14,
  animation: `${floatHeart} ${5 + Math.random() * 4}s ease-in-out ${delay}s infinite`,
  pointerEvents: "none",
  zIndex: 1,
  opacity: 0.5,
  color: ANNIVERSARY_PALETTE.primaryLight,
}));

// ─── STYLED COMPONENTS ───────────────────────────────────────────────────────

const HeroHeader = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  background: `linear-gradient(160deg, ${ANNIVERSARY_PALETTE.glow} 0%, ${alpha(ANNIVERSARY_PALETTE.primary, 0.04)} 40%, ${alpha(ANNIVERSARY_PALETTE.accent, 0.03)} 100%)`,
  padding: theme.spacing(4),
  borderRadius: 28,
  marginBottom: theme.spacing(3),
  border: `1px solid ${alpha(ANNIVERSARY_PALETTE.primary, 0.1)}`,
  animation: `${gentleGlow} 4s ease-in-out infinite`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: -80,
    right: -50,
    width: 250,
    height: 250,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${alpha(ANNIVERSARY_PALETTE.primaryLight, 0.12)} 0%, transparent 70%)`,
    animation: `${floatHeart} 7s ease-in-out infinite`,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -60,
    left: -30,
    width: 180,
    height: 180,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${alpha(ANNIVERSARY_PALETTE.accent, 0.08)} 0%, transparent 70%)`,
    animation: `${floatHeart} 9s ease-in-out infinite 2s`,
  },
}));

const StatPill = styled(Box)(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: 50,
  background: active
    ? `linear-gradient(135deg, ${ANNIVERSARY_PALETTE.primary}, ${ANNIVERSARY_PALETTE.accent})`
    : alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(10px)",
  border: `1.5px solid ${active
    ? "transparent"
    : alpha(ANNIVERSARY_PALETTE.primary, 0.12)}`,
  cursor: "pointer",
  transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: active
    ? `0 6px 20px ${alpha(ANNIVERSARY_PALETTE.primary, 0.3)}`
    : "none",
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: alpha(ANNIVERSARY_PALETTE.primary, 0.35),
    boxShadow: `0 4px 16px ${alpha(ANNIVERSARY_PALETTE.primary, 0.15)}`,
  },
}));

const GlassCard = styled(Card)(({ theme, istoday }) => ({
  borderRadius: 22,
  background: istoday === "true"
    ? `linear-gradient(145deg, ${alpha(ANNIVERSARY_PALETTE.warmGlow, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.97)} 100%)`
    : alpha(theme.palette.background.paper, 0.88),
  backdropFilter: "blur(20px)",
  border: `1px solid ${istoday === "true"
    ? alpha(ANNIVERSARY_PALETTE.primary, 0.2)
    : alpha(theme.palette.divider, 0.25)}`,
  boxShadow: istoday === "true"
    ? `0 8px 32px ${alpha(ANNIVERSARY_PALETTE.primary, 0.1)}, inset 0 1px 0 ${alpha(ANNIVERSARY_PALETTE.primaryLight, 0.15)}`
    : `0 1px 8px ${alpha(theme.palette.common.black, 0.03)}`,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "visible",
  height: "100%",
  "&::before": istoday === "true" ? {
    content: '""',
    position: "absolute",
    inset: -1,
    borderRadius: 23,
    padding: 1,
    background: `linear-gradient(135deg, ${alpha(ANNIVERSARY_PALETTE.primary, 0.3)}, ${alpha(ANNIVERSARY_PALETTE.accent, 0.15)}, transparent)`,
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    pointerEvents: "none",
  } : {},
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: `0 24px 48px ${alpha(ANNIVERSARY_PALETTE.primary, 0.15)}`,
    borderColor: alpha(ANNIVERSARY_PALETTE.primary, 0.35),
    "& .ribbon-glow": {
      width: "100%",
    },
    "& .heart-icon": {
      animation: `${heartbeat} 1.2s ease-in-out infinite`,
    },
    "& .view-arrow": {
      transform: "translateX(4px)",
      opacity: 1,
    },
  },
}));

const RibbonGlow = styled("div")({
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "0%",
  height: 3,
  borderRadius: "0 0 22px 22px",
  background: `linear-gradient(90deg, ${ANNIVERSARY_PALETTE.primary}, ${ANNIVERSARY_PALETTE.accentLight}, ${ANNIVERSARY_PALETTE.primary})`,
  backgroundSize: "200% auto",
  animation: `${shimmer} 2.5s linear infinite`,
  transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
});

const TodayPulse = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -8,
  right: 16,
  display: "flex",
  alignItems: "center",
  gap: 5,
  padding: theme.spacing(0.5, 1.5),
  borderRadius: "0 0 12px 12px",
  background: `linear-gradient(135deg, ${ANNIVERSARY_PALETTE.primary}, ${ANNIVERSARY_PALETTE.accent})`,
  color: "#fff",
  fontSize: "0.62rem",
  fontWeight: 700,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  boxShadow: `0 4px 12px ${alpha(ANNIVERSARY_PALETTE.primary, 0.4)}`,
  "& .pulse-dot": {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#fff",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "50%",
      background: "#fff",
      animation: `${ripple} 1.5s ease-out infinite`,
    },
  },
}));

const StyledAvatar = styled(Avatar)(({ theme, istoday }) => ({
  width: 58,
  height: 58,
  border: istoday === "true"
    ? `3px solid ${ANNIVERSARY_PALETTE.primary}`
    : `2px solid ${alpha(theme.palette.divider, 0.25)}`,
  boxShadow: istoday === "true"
    ? `0 0 0 4px ${alpha(ANNIVERSARY_PALETTE.primary, 0.12)}`
    : `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
  transition: "all 0.3s ease",
}));

const MilestoneBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderRadius: 12,
  background: alpha(ANNIVERSARY_PALETTE.primary, 0.04),
  border: `1px solid ${alpha(ANNIVERSARY_PALETTE.primary, 0.08)}`,
}));

const ActionButton = styled(IconButton)(({ theme, variant }) => ({
  width: 36,
  height: 36,
  borderRadius: 12,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  ...(variant === "whatsapp" && {
    background: alpha("#25D366", 0.08),
    color: "#25D366",
    border: `1px solid ${alpha("#25D366", 0.15)}`,
    "&:hover": {
      background: "#25D366",
      color: "#fff",
      transform: "scale(1.1) rotate(-5deg)",
      boxShadow: `0 6px 16px ${alpha("#25D366", 0.35)}`,
      border: "1px solid transparent",
    },
  }),
  ...(variant === "phone" && {
    background: alpha(ANNIVERSARY_PALETTE.primary, 0.08),
    color: ANNIVERSARY_PALETTE.primary,
    border: `1px solid ${alpha(ANNIVERSARY_PALETTE.primary, 0.15)}`,
    "&:hover": {
      background: ANNIVERSARY_PALETTE.primary,
      color: "#fff",
      transform: "scale(1.1) rotate(5deg)",
      boxShadow: `0 6px 16px ${alpha(ANNIVERSARY_PALETTE.primary, 0.35)}`,
      border: "1px solid transparent",
    },
  }),
}));

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 16,
    background: alpha(theme.palette.background.paper, 0.75),
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    border: `1.5px solid ${alpha(ANNIVERSARY_PALETTE.primary, 0.1)}`,
    "&:hover": {
      borderColor: alpha(ANNIVERSARY_PALETTE.primary, 0.25),
      boxShadow: `0 0 0 4px ${alpha(ANNIVERSARY_PALETTE.primary, 0.05)}`,
    },
    "&.Mui-focused": {
      borderColor: ANNIVERSARY_PALETTE.primary,
      boxShadow: `0 0 0 4px ${alpha(ANNIVERSARY_PALETTE.primary, 0.1)}`,
    },
    "& fieldset": { border: "none" },
  },
}));

// ─── FLOATING HEARTS BACKGROUND ─────────────────────────────────────────────

const HeartsBackground = React.memo(() => {
  const hearts = useMemo(() =>
    Array.from({ length: 8 }, (_, i) => ({
      id: i,
      left: 5 + Math.random() * 90,
      delay: Math.random() * 4,
      size: 12 + Math.random() * 10,
    })), []
  );

  return (
    <>
      {hearts.map((h) => (
        <FloatingHeart key={h.id} left={h.left} delay={h.delay} size={h.size}>
          ♥
        </FloatingHeart>
      ))}
    </>
  );
});

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────

export default function Anniversary() {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [day, setDay] = useState("Today");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [allCustomers, setAllCustomers] = useState([]);

  // Fetch data ONLY once on mount
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axiosPrivate.post("LoadReferenceAnniversary.php");
        const result = response.data;
        if (isMounted) {
          if (Array.isArray(result.Data)) {
            setAllCustomers(result.Data);
          } else {
            setAllCustomers([]);
            console.error("Unexpected data format:", result);
          }
        }
      } catch (error) {
        console.error("Error fetching greetings data:", error);
        if (isMounted) setAllCustomers([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [axiosPrivate]);

  // Compute stats
  const stats = useMemo(() => {
    const today = allCustomers.filter((c) => c?.DateCategory === "Today").length;
    const tomorrow = allCustomers.filter((c) => c?.DateCategory === "Tomorrow").length;
    const total = allCustomers.length;
    return { today, tomorrow, total };
  }, [allCustomers]);

  // Client-side filtering
  const filteredCustomers = useMemo(() => {
    return allCustomers.filter((c) => {
      if (day !== "All" && c?.DateCategory !== day) return false;
      if (search) {
        const searchLower = search.toLowerCase();
        const nameMatch = (c?.FamilyMember || "").toLowerCase().includes(searchLower);
        const mobileMatch = (c?.Mobile || "").toLowerCase().includes(searchLower);
        if (!nameMatch && !mobileMatch) return false;
      }
      return true;
    });
  }, [allCustomers, day, search]);

  const handleWhatsApp = useCallback((mobile, name) => {
    if (!mobile) return;
    const message = encodeURIComponent(`Happy Anniversary ${name}! 💑🎉 Wishing you many more years of love and togetherness!`);
    window.open(`https://wa.me/91${mobile}?text=${message}`, "_blank");
  }, []);

  const handleCall = useCallback((mobile) => {
    if (!mobile) return;
    window.open(`tel:${mobile}`);
  }, []);

  const filters = [
    { label: "Today", value: "Today", icon: <Today sx={{ fontSize: 16 }} />, count: stats.today },
    { label: "Tomorrow", value: "Tomorrow", icon: <DateRange sx={{ fontSize: 16 }} />, count: stats.tomorrow },
    { label: "All", value: "All", icon: <AllInclusive sx={{ fontSize: 16 }} />, count: stats.total },
  ];

  // Milestone label helper
  const getMilestoneLabel = (years) => {
    const y = parseInt(years);
    if (y === 25) return "Silver Jubilee 🥈";
    if (y === 50) return "Golden Jubilee 🥇";
    if (y === 1) return "1st Anniversary";
    if (y === 5) return "Wood Anniversary";
    if (y === 10) return "Tin Anniversary";
    return `${years} Years Together`;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, margin: "0 auto" }}>
      {/* ── HERO HEADER ── */}
      <HeroHeader>
        <HeartsBackground />

        <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 2 }}>
          {/* Title */}
          <Grid item xs={12} md={4}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: "16px",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: `linear-gradient(135deg, ${ANNIVERSARY_PALETTE.primary}, ${ANNIVERSARY_PALETTE.accent})`,
                boxShadow: `0 6px 20px ${alpha(ANNIVERSARY_PALETTE.primary, 0.35)}`,
                position: "relative",
              }}>
                <Favorite sx={{ color: "#fff", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${ANNIVERSARY_PALETTE.primaryDark}, ${ANNIVERSARY_PALETTE.accent})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.02em",
                }}>
                  Anniversary Wishes
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25, fontSize: "0.8rem" }}>
                  Celebrate love milestones with your clients 💕
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Filter Pills */}
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "center" }}>
              {filters.map((f) => (
                <StatPill
                  key={f.value}
                  active={day === f.value ? 1 : undefined}
                  onClick={() => setDay(f.value)}
                >
                  {React.cloneElement(f.icon, {
                    sx: {
                      fontSize: 16,
                      color: day === f.value ? "#fff" : ANNIVERSARY_PALETTE.primary,
                    },
                  })}
                  <Typography sx={{
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    color: day === f.value ? "#fff" : "text.primary",
                  }}>
                    {f.label}
                  </Typography>
                  <Box sx={{
                    px: 0.8,
                    py: 0.15,
                    borderRadius: 6,
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    background: day === f.value
                      ? alpha("#fff", 0.25)
                      : alpha(ANNIVERSARY_PALETTE.primary, 0.1),
                    color: day === f.value ? "#fff" : ANNIVERSARY_PALETTE.primary,
                    minWidth: 22,
                    textAlign: "center",
                  }}>
                    {f.count}
                  </Box>
                </StatPill>
              ))}
            </Stack>
          </Grid>

          {/* Search */}
          <Grid item xs={12} md={4}>
            <SearchField
              placeholder="Search name or mobile..."
              variant="outlined"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: ANNIVERSARY_PALETTE.primary, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </HeroHeader>

      {/* ── CONTENT GRID ── */}
      {loading ? (
        <Grid container spacing={2.5}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
              <Skeleton
                variant="rounded"
                height={220}
                sx={{
                  borderRadius: 5.5,
                  background: `linear-gradient(90deg, ${alpha(ANNIVERSARY_PALETTE.primary, 0.03)} 25%, ${alpha(ANNIVERSARY_PALETTE.primary, 0.07)} 50%, ${alpha(ANNIVERSARY_PALETTE.primary, 0.03)} 75%)`,
                  backgroundSize: "200% 100%",
                  animation: `${shimmer} 1.5s ease-in-out infinite`,
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : filteredCustomers.length === 0 ? (
        <Box sx={{
          textAlign: "center",
          py: 12,
          px: 3,
          borderRadius: 7,
          background: `linear-gradient(180deg, ${alpha(ANNIVERSARY_PALETTE.primary, 0.02)}, transparent)`,
          border: `2px dashed ${alpha(ANNIVERSARY_PALETTE.primary, 0.1)}`,
        }}>
          <Box sx={{
            width: 88, height: 88, borderRadius: "50%", mx: "auto", mb: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha(ANNIVERSARY_PALETTE.primary, 0.06)}, ${alpha(ANNIVERSARY_PALETTE.accent, 0.04)})`,
            border: `2px solid ${alpha(ANNIVERSARY_PALETTE.primary, 0.08)}`,
          }}>
            <FavoriteBorder sx={{ fontSize: 42, color: alpha(ANNIVERSARY_PALETTE.primary, 0.35) }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "text.secondary", mb: 1 }}>
            No anniversaries found
          </Typography>
          <Typography variant="body2" sx={{ color: "text.disabled", maxWidth: 340, mx: "auto" }}>
            Switch filters or update your search to discover upcoming celebrations.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filteredCustomers.map((c, index) => {
            const isToday = c?.DateCategory === "Today";
            const yearsNum = parseInt(c.YearsCompleted) || 0;
            const pictureSrc = c.Picture
              ? c.Picture.startsWith("http")
                ? c.Picture
                : `https://backend.dahiyainsurance.com/Upload/profile/${c.Picture}`
              : null;

            // Progress towards a milestone (next multiple of 5)
            const nextMilestone = Math.ceil((yearsNum + 1) / 5) * 5;
            const progress = (yearsNum / nextMilestone) * 100;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${c?.ID}-${index}`}>
                <Grow in timeout={350 + index * 70} style={{ transformOrigin: "50% 100%" }}>
                  <div style={{ height: "100%" }}>
                    <GlassCard istoday={isToday.toString()}>
                      <RibbonGlow className="ribbon-glow" />

                      {/* Today Pulse Tag */}
                      {isToday && (
                        <TodayPulse>
                          <div className="pulse-dot" />
                          TODAY
                        </TodayPulse>
                      )}

                      <CardContent sx={{ p: 2.5, pb: "16px !important", overflow: "visible" }}>
                        {/* Profile Row */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, mt: isToday ? 0.5 : 0 }}>
                          <Box sx={{ position: "relative" }}>
                            <StyledAvatar
                              src={pictureSrc || undefined}
                              alt={c.FamilyMember}
                              istoday={isToday.toString()}
                              sx={{
                                ...(!pictureSrc && {
                                  background: `linear-gradient(135deg, ${alpha(ANNIVERSARY_PALETTE.primary, 0.1)}, ${alpha(ANNIVERSARY_PALETTE.accent, 0.06)})`,
                                  color: ANNIVERSARY_PALETTE.primary,
                                }),
                              }}
                            >
                              {!pictureSrc && <VolunteerActivismOutlined sx={{ fontSize: 26 }} />}
                            </StyledAvatar>
                            {/* Heart badge on avatar */}
                            {isToday && (
                              <Box sx={{
                                position: "absolute",
                                bottom: -2,
                                right: -2,
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                background: `linear-gradient(135deg, ${ANNIVERSARY_PALETTE.primary}, ${ANNIVERSARY_PALETTE.accent})`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: `0 2px 6px ${alpha(ANNIVERSARY_PALETTE.primary, 0.4)}`,
                                border: `2px solid ${theme.palette.background.paper}`,
                              }}>
                                <Favorite className="heart-icon" sx={{ fontSize: 11, color: "#fff" }} />
                              </Box>
                            )}
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                textTransform: "capitalize",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: "0.95rem",
                                lineHeight: 1.3,
                              }}
                            >
                              {(c?.FamilyMember || "Unknown").toLowerCase()}
                            </Typography>

                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.4 }}>
                              <Phone sx={{ fontSize: 12, color: "text.disabled" }} />
                              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.73rem" }}>
                                {c?.Mobile || "No Mobile"}
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>

                        {/* Milestone Bar */}
                        <MilestoneBar sx={{ mb: 2 }}>
                          <CelebrationOutlined sx={{ fontSize: 16, color: ANNIVERSARY_PALETTE.primary }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              color: ANNIVERSARY_PALETTE.primaryDark,
                              lineHeight: 1.2,
                            }}>
                              {getMilestoneLabel(c.YearsCompleted)}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.3 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(progress, 100)}
                                sx={{
                                  flex: 1,
                                  height: 4,
                                  borderRadius: 4,
                                  bgcolor: alpha(ANNIVERSARY_PALETTE.primary, 0.08),
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    background: `linear-gradient(90deg, ${ANNIVERSARY_PALETTE.primary}, ${ANNIVERSARY_PALETTE.accent})`,
                                  },
                                }}
                              />
                              <Typography sx={{
                                fontSize: "0.6rem",
                                color: "text.disabled",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}>
                                → {nextMilestone}yr
                              </Typography>
                            </Stack>
                          </Box>
                        </MilestoneBar>

                        {/* Date Chip */}
                        <Chip
                          size="small"
                          icon={<CalendarMonthOutlined sx={{ fontSize: 13, color: "text.disabled !important" }} />}
                          label={moment(c?.Date).format("DD MMM YYYY")}
                          sx={{
                            fontSize: "0.7rem",
                            height: 26,
                            borderRadius: "8px",
                            color: "text.secondary",
                            background: alpha(theme.palette.action.hover, 0.4),
                            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                            mb: 2,
                          }}
                        />

                        {/* Divider */}
                        <Divider sx={{
                          borderColor: alpha(theme.palette.divider, 0.06),
                          mb: 1.5,
                        }} />

                        {/* Actions Row */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" spacing={0.8}>
                            <Tooltip title="Send Anniversary Wishes" arrow placement="top">
                              <ActionButton
                                variant="whatsapp"
                                size="small"
                                onClick={() => handleWhatsApp(c?.Mobile, (c?.FamilyMember || "").split(" ")[0])}
                              >
                                <WhatsApp sx={{ fontSize: 17 }} />
                              </ActionButton>
                            </Tooltip>
                            <Tooltip title="Call" arrow placement="top">
                              <ActionButton
                                variant="phone"
                                size="small"
                                onClick={() => handleCall(c?.Mobile)}
                              >
                                <Phone sx={{ fontSize: 17 }} />
                              </ActionButton>
                            </Tooltip>
                          </Stack>

                          <Button
                            component={Link}
                            to={
                              c?.Type === "Agent"
                                ? `/CLIAProspect/EditCLIAProspect/${c?.MainMemberID}`
                                : `/Prospects/ViewProspect/${c?.MainMemberID}`
                            }
                            size="small"
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.78rem",
                              color: "text.secondary",
                              borderRadius: 2,
                              px: 1.5,
                              "&:hover": {
                                color: ANNIVERSARY_PALETTE.primary,
                                background: alpha(ANNIVERSARY_PALETTE.primary, 0.05),
                              },
                            }}
                            endIcon={
                              <ArrowForward
                                className="view-arrow"
                                sx={{
                                  fontSize: "14px !important",
                                  transition: "all 0.3s ease",
                                  opacity: 0.5,
                                }}
                              />
                            }
                          >
                            View
                          </Button>
                        </Stack>
                      </CardContent>
                    </GlassCard>
                  </div>
                </Grow>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
